import React, {useState, useEffect} from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Fade,
  Modal,
} from "react-bootstrap";
import { ArrowRight, X, PlayCircle } from "react-bootstrap-icons";

import resourcesList, { ResourceData } from "./resourcesList";
import { routes } from "../../utils";
import FadeWrapper from "../FadeWrapper";
import formatDate from "./utils/formatDate";
import useResourcesFiltersAndSort from "./useResourcesFiltersAndSort";
import ResourcesSearchInput from "./ResourcesSearchInput";
import ResourcesSortDropdown from "./ResourcesSortDropdown";

const categories = ["All", "Software Tools", "PCR", "Molecular Cloning", "Video", "History of Science"];

export default function ArticlesOverview() {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const {
    query,
    setQuery,
    activeCategory,
    setActiveCategory,
    sortOption,
    setSortOption,
    result: sorted,
  } = useResourcesFiltersAndSort<ResourceData>({
    items: resourcesList,
    getTitle: (r) => r.title,
    getSearchText: (r) => r.shortDescription,
    getDate: (r) => r.date,
    matchesCategory: (r, cat) =>
      cat === "All" ? true : cat === "Video" ? !!r.youtubeUrl : r.category === cat,
  });

  /**
   * Adding lazy loading for image/video thumbnails
   * need time to load video thumbnails in div background, use lazy loading here to improve performance
   */
  useEffect(() => {
    const lazyDivs = document.querySelectorAll<HTMLDivElement>(".lazy-bg");
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target as HTMLDivElement;
          const bg = el.getAttribute("data-bg");
          if (bg) el.style.backgroundImage = `url(${bg})`;
          obs.unobserve(el);
        }
      });
    });
    lazyDivs.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sorted]);

  return (
    <FadeWrapper>
      {/* Banner image is required here, future work! */}
      <div
        className="py-5 text-center text-white"
        style={{ background: "#1d446b" }}
      >
        <h1 className="fw-bold mb-2 display-5">Articles</h1>
        <p className="opacity-75 fs-5">
          Explore expert-written articles on molecular biology, cloning, and lab software tools.
        </p>
        <ResourcesSearchInput query={query} setQuery={setQuery} />
      </div>

      {/* Main */}
      <Container fluid className="pt-3 pb-5 px-md-5" style={{ minHeight: "1000px" }}>
        <Row className="gy-4 gx-5">
          {/* Sidebar */}
          <Col md={3} className="border-end pe-md-4" style={{ minWidth: 250 }}>
            <div className="sticky-top sticky-top-offset">
              <h6 className="fw-semibold mb-3">Filter by Category</h6>
              <div className="d-flex flex-wrap gap-2 mb-4">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    size="sm"
                    variant={cat === activeCategory ? "primary" : "outline-primary"}
                    onClick={() => setActiveCategory(cat)}
                    className="rounded-pill px-3"
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          </Col>

          <Col md={9}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="small text-muted">
                Showing {sorted.length} article{sorted.length !== 1 && "s"}
              </div>
              <ResourcesSortDropdown sortOption={sortOption} setSortOption={setSortOption} />
            </div>

            {sorted.length === 0 ? (
              <p className="text-center text-muted py-5 fs-5">
                No articles found matching your search or filters.
              </p>
            ) : (
              <Row xs={1} sm={2} lg={3} className="g-4">
                {sorted.map((resource, idx) => (
                  <Fade in appear timeout={200 + idx * 100} key={resource.href}>
                    <Col>
                      <Card
                        className="h-100 shadow-sm border-0 rounded-3 overflow-hidden resource-card"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (resource.youtubeUrl) setVideoUrl(resource.youtubeUrl);
                          else window.location.href = routes.resourceView(resource.href);
                        }}
                      >
                        <div className="resource-image-container">
                          <div
                            className="resource-image lazy-bg"
                            data-bg={resource.image || "/images/article-placeholder.jpg"}
                          />
                          {resource.youtubeUrl && (
                            <div className="resource-video-overlay">
                              <PlayCircle size={50} className="text-white opacity-90" />
                            </div>
                          )}
                          <div className="resource-cta">
                            <span className="btn btn-sm btn-light rounded-pill px-3">
                              {resource.youtubeUrl ? "Watch Video" : "Read Article"}{" "}
                              <ArrowRight className="ms-1" />
                            </span>
                          </div>
                        </div>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span
                              className="badge bg-light text-primary border"
                              style={{ fontSize: "0.75rem", fontWeight: 500 }}
                            >
                              {resource.category}
                            </span>
                          </div>
                          <Card.Title className="fw-bold mb-1 fs-6">{resource.title}</Card.Title>
                          <div className="text-muted mb-2" style={{ fontSize: "0.8rem" }}>
                            {resource.readTime} • {formatDate(resource.date)}
                          </div>
                          <Card.Text className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                            {resource.shortDescription}
                          </Card.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Fade>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      {/*  YouTube video embedded Modal */}
      <Modal
        show={!!videoUrl}
        onHide={() => setVideoUrl(null)}
        centered
        size="lg"
        contentClassName="border-0 rounded-4 overflow-hidden bg-dark"
      >
        <Modal.Header className="border-0 bg-dark text-white d-flex justify-content-between align-items-center">
          <Modal.Title>🎥 Watch Video</Modal.Title>
          <Button variant="outline-light" size="sm" onClick={() => setVideoUrl(null)}>
            <X />
          </Button>
        </Modal.Header>
        <Modal.Body className="p-0 bg-black">
          {videoUrl && (
            <div className="ratio ratio-16x9">
              <iframe
                src={`${videoUrl}?autoplay=1`} /** enable auto play when you open the modal */
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ border: 0 }}
              />
            </div>
          )}
        </Modal.Body>
      </Modal>

      <style>{`
        .resource-card { transition: all 0.3s ease; }
        .resource-card:hover { transform: translateY(-6px); box-shadow: 0 0.75rem 1.5rem rgba(0,0,0,0.1); }
        .resource-image-container { position: relative; overflow: hidden; height: 180px; }
        .resource-image {
          width: 100%; height: 100%;
          background-size: cover; background-position: center;
          position: relative; transition: transform 0.4s ease;
        }
        .resource-image::before {
          content: ""; position: absolute; inset: 0;
          background: rgba(0,0,0,0.35); z-index: 1;
        }
        .resource-image::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.4), transparent);
          z-index: 2;
        }
        .resource-card:hover .resource-image { transform: scale(1.08); }
        .resource-cta {
          position: absolute; inset: 0;
          display: flex; align-items: flex-end; justify-content: center;
          padding: 1rem; opacity: 0; transition: opacity 0.25s ease;
          z-index: 3;
        }
        .resource-card:hover .resource-cta { opacity: 1; }
        .resource-video-overlay {
          position: absolute;
          inset: 0;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.9;
          transition: opacity 0.3s ease;
        }
        .resource-card:hover .resource-video-overlay { opacity: 1; }

        /* 🪄 Modal animation */
        .modal.show .modal-dialog {
          transform: scale(1.02);
          animation: modalFadeIn 0.3s ease forwards;
        }
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </FadeWrapper>
  );
}
